import { createContext, useState, useEffect, useCallback, ReactNode } from 'react'
import { authService, User } from '../services/auth'
import api, { authEvents } from '../services/api'

interface AuthContextType {
  user: User | null
  loading: boolean
  error: string | null
  sessionExpired: boolean
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
  checkAuth: () => Promise<void>
  clearSessionExpired: () => void
}

export const AuthContext = createContext<AuthContextType | null>(null)

interface AuthProviderProps {
  children: ReactNode
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<User | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sessionExpired, setSessionExpired] = useState(false)

  const checkAuth = async () => {
    try {
      setLoading(true)
      setError(null)
      const currentUser = await authService.checkAuth()
      setUser(currentUser)

      // Fetch CSRF token if authenticated
      if (currentUser) {
        await api.fetchCsrfToken()
      }
    } catch (err) {
      // Log in development only
      if (import.meta.env.DEV) {
        console.error('Auth check failed:', err)
      }

      // Differentiate between expected logout and actual errors
      if (err instanceof Error) {
        if (err.message.includes('401') || err.message.includes('Unauthorized')) {
          // Expected: user not logged in
          setUser(null)
          setError(null) // Don't show error for expected logout
        } else if (err.message.includes('Network') || err.message.includes('Failed to fetch')) {
          // Network error: show message
          setError('Erreur de connexion. Vérifiez votre connexion internet.')
          setUser(null)
        } else {
          // Unexpected error: log and show generic message
          setError('Erreur lors de la vérification de l\'authentification.')
          setUser(null)
        }
      } else {
        // Unknown error type
        setUser(null)
        setError('Erreur inattendue.')
      }
    } finally {
      setLoading(false)
    }
  }

  // Handle session expiration from any API call
  const handleUnauthorized = useCallback(() => {
    setUser(null)
    setError(null)
    setSessionExpired(true)
  }, [])

  const clearSessionExpired = useCallback(() => {
    setSessionExpired(false)
  }, [])

  useEffect(() => {
    checkAuth()
  }, [])

  // Subscribe to 401 events from API service
  useEffect(() => {
    const unsubscribe = authEvents.onUnauthorized(handleUnauthorized)
    return unsubscribe
  }, [handleUnauthorized])

  const login = async (email: string, password: string) => {
    try {
      setError(null)
      setSessionExpired(false)
      const user = await authService.login(email, password)
      setUser(user)

      // Fetch CSRF token after successful login
      await api.fetchCsrfToken()
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Login failed:', err)
      }

      if (err instanceof Error) {
        setError(err.message)
      } else {
        setError('Échec de la connexion. Vérifiez vos identifiants.')
      }
      throw err // Re-throw so the login component can handle it
    }
  }

  const logout = async () => {
    try {
      await authService.logout()
    } catch (err) {
      if (import.meta.env.DEV) {
        console.error('Logout failed:', err)
      }
      // Don't block logout even if API call fails
    } finally {
      setUser(null)
      setError(null)
    }
  }

  return (
    <AuthContext.Provider value={{ user, loading, error, sessionExpired, login, logout, checkAuth, clearSessionExpired }}>
      {children}
    </AuthContext.Provider>
  )
}
